import { useEffect } from 'react';

export interface UseUnsavedChangesOptions {
    isDirty: boolean;
    enabled?: boolean;
}

/**
 * Hook to warn users about unsaved changes when leaving the page.
 *
 * Handles browser refresh/close with the native "Leave site?" dialog.
 * For in-app navigation blocking, components should implement their own
 * dialog logic since BrowserRouter doesn't support useBlocker.
 */
export const useUnsavedChanges = ({
    isDirty,
    enabled = true
}: UseUnsavedChangesOptions): void => {
    const shouldBlock = isDirty && enabled;

    // Log when dirty state changes
    useEffect(() => {
        console.log('[useUnsavedChanges] isDirty:', isDirty, 'enabled:', enabled, 'shouldBlock:', shouldBlock);
    }, [isDirty, enabled, shouldBlock]);

    // Handle browser refresh/close with beforeunload
    useEffect(() => {
        if (!shouldBlock) {
            console.log('[useUnsavedChanges] Not blocking - shouldBlock is false');
            return;
        }

        console.log('[useUnsavedChanges] Adding beforeunload listener');
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            console.log('[useUnsavedChanges] beforeunload triggered!');
            e.preventDefault();
            // Modern browsers require returnValue to be set
            e.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            console.log('[useUnsavedChanges] Removing beforeunload listener');
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [shouldBlock]);
};
